import { useState, useEffect } from 'react';
import { Table, Input, Row, Col, Spinner } from 'reactstrap';
import { Edit, RefreshCw, Plus, Trash2 } from 'react-feather';
import { toast } from 'react-toastify';
import { Btn } from '../../../AbstractElements';
import AppVersionService from '../../../Api/services/app-version.service';
import type { components } from '../../../Api/types/openapi';
import AppVersionFormModal from './AppVersionFormModal';
import DeleteConfirmModal from './DeleteConfirmModal';
import { useTranslation } from 'react-i18next';

type AppVersionResponse = components['schemas']['AppVersionResponse'];
type PaginationAppVersionResponse = components['schemas']['Pagination_AppVersionResponse_'];

const AppVersionsList = () => {
  const { t } = useTranslation();
  const [versions, setVersions] = useState<AppVersionResponse[]>([]);
  const [pagination, setPagination] = useState<PaginationAppVersionResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(10);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<AppVersionResponse | null>(null);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    loadVersions();
  }, [currentPage, searchTerm]);

  const loadVersions = async () => {
    try {
      setLoading(true);
      const params: any = {
        page: currentPage,
        limit,
      };

      if (searchTerm) {
        params.version = searchTerm;
      }

      const data = await AppVersionService.getAppVersionsPaginated(params);
      
      if (!data || !Array.isArray(data.results)) {
        console.warn('Respuesta inválida del servidor: results no es un array');
        setVersions([]);
        setPagination(null);
        return;
      }
      
      setVersions(data.results);
      setPagination(data);
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message || t('Error al cargar versiones');
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedVersion(null);
    setIsEditing(false);
    setShowModal(true);
  };

  const handleEdit = (version: AppVersionResponse) => {
    setSelectedVersion(version);
    setIsEditing(true);
    setShowModal(true);
  };

  const handleDelete = (version: AppVersionResponse) => {
    setSelectedVersion(version);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!selectedVersion?.id) return;

    try {
      await AppVersionService.deleteAppVersion(selectedVersion.id);
      toast.success(t('Versión eliminada exitosamente'));
      setShowDeleteModal(false);
      setSelectedVersion(null);
      loadVersions();
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message || t('Error al eliminar versión');
      toast.error(errorMessage);
    }
  };

  const handleToggleState = async (version: AppVersionResponse) => {
    if (!version.id) return;

    try {
      await AppVersionService.changeAppVersionState(version.id);
      toast.success(t('Estado de la versión actualizado'));
      loadVersions();
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message || t('Error al cambiar estado');
      toast.error(errorMessage);
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setSelectedVersion(null);
    setIsEditing(false);
  };

  const handleModalSuccess = () => {
    handleModalClose();
    loadVersions();
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('es-ES', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  return (
    <div>
      <Row className="mb-3">
        <Col md="6">
          <Input
            type="text"
            placeholder={t('Buscar por versión...')}
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
          />
        </Col>
        <Col md="6" className="text-end">
          <Btn color="primary" className="me-2" onClick={handleCreate}>
            <Plus size={16} className="me-1" />
            {t('Crear Nueva Versión')}
          </Btn>
          <Btn color="secondary" onClick={loadVersions} className="me-2">
            <RefreshCw size={16} className="me-1" />
            {t('Actualizar')}
          </Btn>
        </Col>
      </Row>

      {loading ? (
        <div className="text-center py-5">
          <Spinner color="primary" />
        </div>
      ) : (
        <>
          <Table responsive striped>
            <thead>
              <tr>
                <th>{t('ID')}</th>
                <th>{t('Versión')}</th>
                <th>{t('URL')}</th>
                <th>{t('Descripción')}</th>
                <th>{t('Estado')}</th>
                <th>{t('Fecha de Creación')}</th>
                <th>{t('Acciones')}</th>
              </tr>
            </thead>
            <tbody>
              {versions.length === 0 ? (
                <tr>
                  <td colSpan={7} className="text-center">
                    {t('No se encontraron versiones')}
                  </td>
                </tr>
              ) : (
                versions.map((version) => (
                  <tr key={version.id}>
                    <td>{version.id}</td>
                    <td>
                      <strong>{version.version}</strong>
                    </td>
                    <td>
                      <a href={version.url} target="_blank" rel="noopener noreferrer" className="text-primary">
                        {version.url.length > 50 ? `${version.url.substring(0, 50)}...` : version.url}
                      </a>
                    </td>
                    <td>{version.description || '-'}</td>
                    <td>
                      <div className="form-check form-switch">
                        <Input
                          type="checkbox"
                          className="form-check-input"
                          checked={version.active || false}
                          onChange={() => handleToggleState(version)}
                        />
                      </div>
                    </td>
                    <td>{formatDate(version.created_at)}</td>
                    <td>
                      <Btn
                        color="primary"
                        size="sm"
                        className="me-1 btn_list_action"
                        onClick={() => handleEdit(version)}
                      >
                        <Edit size={14} />
                      </Btn>
                      <Btn
                        color="danger"
                        size="sm"
                        className="btn_list_action"
                        onClick={() => handleDelete(version)}
                      >
                        <Trash2 size={14} />
                      </Btn>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>

          {pagination && pagination.pages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-3">
              <div>
                {t('Página')} {pagination.current_page} {t('de')} {pagination.pages}
              </div>
              <div>
                <Btn
                  color="primary"
                  size="sm"
                  disabled={currentPage === 1}
                  onClick={() => handlePageChange(currentPage - 1)}
                  className="me-2"
                >
                  {t('Anterior')}
                </Btn>
                <Btn
                  color="primary"
                  size="sm"
                  disabled={currentPage === pagination.pages}
                  onClick={() => handlePageChange(currentPage + 1)}
                >
                  {t('Siguiente')}
                </Btn>
              </div>
            </div>
          )}
        </>
      )}

      <AppVersionFormModal
        isOpen={showModal}
        toggle={handleModalClose}
        version={selectedVersion}
        isEditing={isEditing}
        onSuccess={handleModalSuccess}
      />

      <DeleteConfirmModal
        isOpen={showDeleteModal}
        toggle={() => {
          setShowDeleteModal(false);
          setSelectedVersion(null);
        }}
        version={selectedVersion}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
};

export default AppVersionsList;

